import type { CSSProperties, ReactElement } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "./auth/useAuth";
import { useServiceInfo, useSession } from "./session/hooks";

const APP_NAME = "Galileo · Office";

const layoutStyle: CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};

const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    padding: "16px 24px",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
};

const brandStyle: CSSProperties = {
    fontSize: "1.05rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
};

const userBlockStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "0.9rem",
};

const signOutStyle: CSSProperties = {
    padding: "6px 12px",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    background: "transparent",
    color: "inherit",
    fontSize: "0.85rem",
    cursor: "pointer",
};

const mainStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    // The shell now anchors to the viewport (see App.tsx #root), so this
    // landing page is responsible for its own internal scroll. Without
    // overflow: auto the hero + status cards + session block would be
    // clipped on short viewports; with it, the header stays pinned and
    // only the content area scrolls.
    overflow: "auto",
    padding: "32px clamp(16px, 4vw, 24px)",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    maxWidth: "980px",
    width: "100%",
    margin: "0 auto",
};

const heroStyle: CSSProperties = {
    padding: "24px",
    borderRadius: "16px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(255, 255, 255, 0.10)",
};

const eyebrowStyle: CSSProperties = {
    margin: "0 0 8px",
    fontSize: "0.85rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "rgba(255, 255, 255, 0.55)",
};

const titleStyle: CSSProperties = {
    margin: 0,
    fontSize: "2rem",
    lineHeight: 1.15,
    letterSpacing: "-0.03em",
};

const subtitleStyle: CSSProperties = {
    margin: "10px 0 0",
    color: "rgba(255,255,255,0.66)",
    maxWidth: "680px",
};

const statusGridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
};

const statusCardStyle: CSSProperties = {
    padding: "18px",
    borderRadius: "14px",
    background: "rgba(255, 255, 255, 0.035)",
    border: "1px solid rgba(255, 255, 255, 0.10)",
};

const labelStyle: CSSProperties = {
    display: "block",
    marginBottom: "8px",
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
};

const valueStyle: CSSProperties = {
    margin: 0,
    color: "rgba(255,255,255,0.92)",
    fontSize: "0.98rem",
    overflowWrap: "anywhere",
};

const badgeBaseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "0.78rem",
    fontWeight: 700,
};

const detailsStyle: CSSProperties = {
    color: "rgba(255,255,255,0.58)",
    fontSize: "0.85rem",
    marginTop: "12px",
};

const ctaLinkStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "16px",
    padding: "10px 16px",
    borderRadius: "10px",
    background: "oklch(0.78 0.13 260)",
    color: "oklch(0.18 0.03 260)",
    fontWeight: 600,
    textDecoration: "none",
};

/**
 * Iteration-0 authenticated landing page. It proves the sign-in and API
 * path without exposing raw debug JSON to normal users.
 */
export function AuthenticatedShell(): ReactElement {
    const auth = useAuth();
    const sessionQuery = useSession();
    const serviceInfoQuery = useServiceInfo();

    function handleSignOut(): void {
        void auth.signOut();
    }

    return (
        <div style={layoutStyle}>
            <header style={headerStyle}>
                <div style={brandStyle}>{APP_NAME}</div>
                <div style={userBlockStyle}>
                    <span>{auth.account?.name ?? auth.account?.username ?? ""}</span>
                    <button type="button" style={signOutStyle} onClick={handleSignOut}>
                        Sign out
                    </button>
                </div>
            </header>

            <main style={mainStyle}>
                <section style={heroStyle}>
                    <p style={eyebrowStyle}>Office</p>
                    <h1 style={titleStyle}>Manage Office master data.</h1>
                    <p style={subtitleStyle}>
                        Sage offices, workspaces, assets, and assignments — all in one place.
                    </p>
                    <Link to="/admin" style={ctaLinkStyle}>
                        Open admin
                        <ArrowRight size={16} />
                    </Link>
                </section>

                <section style={statusGridStyle} aria-label="System status">
                    <StatusCard
                        label="Signed in as"
                        value={auth.account?.name ?? auth.account?.username ?? "Unknown user"}
                    />
                    <StatusCard
                        label="Frontend auth"
                        value={auth.account?.tenantId ? "Connected" : "Unknown"}
                        tone={auth.account?.tenantId ? "ok" : "warn"}
                    />
                    <StatusCard
                        label="API health"
                        value={renderApiHealthValue(serviceInfoQuery)}
                        tone={serviceInfoQuery.isSuccess ? "ok" : serviceInfoQuery.isError ? "error" : "wait"}
                    />
                    <StatusCard
                        label="API session"
                        value={renderSessionValue(sessionQuery)}
                        tone={sessionQuery.isSuccess ? "ok" : sessionQuery.isError ? "error" : "wait"}
                    />
                </section>

                {sessionQuery.isSuccess ? (
                    <section style={heroStyle}>
                        <p style={eyebrowStyle}>Session</p>
                        <p style={valueStyle}>{sessionQuery.data.displayName}</p>
                        <p style={detailsStyle}>
                            {sessionQuery.data.username} · scopes:{" "}
                            {sessionQuery.data.scopes.length > 0
                                ? sessionQuery.data.scopes.join(", ")
                                : "none returned"}
                        </p>
                    </section>
                ) : null}
            </main>
        </div>
    );
}

interface StatusCardProps {
    readonly label: string;
    readonly value: string;
    readonly tone?: "ok" | "warn" | "error" | "wait";
}

function StatusCard({ label, value, tone }: StatusCardProps): ReactElement {
    const badgeStyle: CSSProperties =
        tone === undefined
            ? valueStyle
            : {
                  ...badgeBaseStyle,
                  background: resolveBadgeBackground(tone),
                  color: resolveBadgeColor(tone),
              };

    return (
        <div style={statusCardStyle}>
            <span style={labelStyle}>{label}</span>
            <p style={badgeStyle}>{value}</p>
        </div>
    );
}

function renderApiHealthValue(query: ReturnType<typeof useServiceInfo>): string {
    if (query.isPending) {
        return "Checking";
    }
    if (query.isError) {
        return "Unavailable";
    }
    return `${query.data.service}: ${query.data.status}`;
}

function renderSessionValue(query: ReturnType<typeof useSession>): string {
    if (query.isPending) {
        return "Checking";
    }
    if (query.isError) {
        return "Unavailable";
    }
    return "Connected";
}

function resolveBadgeBackground(tone: NonNullable<StatusCardProps["tone"]>): string {
    switch (tone) {
        case "ok":
            return "oklch(0.62 0.14 155 / 0.18)";
        case "warn":
            return "oklch(0.78 0.13 85 / 0.18)";
        case "error":
            return "oklch(0.65 0.18 25 / 0.18)";
        case "wait":
            return "rgba(255,255,255,0.10)";
    }
}

function resolveBadgeColor(tone: NonNullable<StatusCardProps["tone"]>): string {
    switch (tone) {
        case "ok":
            return "oklch(0.86 0.11 155)";
        case "warn":
            return "oklch(0.90 0.12 85)";
        case "error":
            return "oklch(0.82 0.15 25)";
        case "wait":
            return "rgba(255,255,255,0.72)";
    }
}
